'use client';

import { useCallback, useEffect, useState } from 'react';
import { type Nullable } from '@libs/core';
import type { CartUI, EstimatedTreatsPoints } from '@libs/common-node';

import { getEstimatedTreatsPointsForCart, logConsoleError } from '../../utils';
import type { IdentityUser } from '../UserSessionProvider';
import { COMPONENTS } from '../../constants';

const useTreatsInCart = (cart: Nullable<CartUI>, user: IdentityUser) => {
    const [estimatedTreatsPoints, setEstimatedTreatsPoints] =
        useState<Nullable<EstimatedTreatsPoints>>(null);
    const [isLoading, setIsLoading] = useState(false);

    const getEstimatedTreatsPoints = useCallback(async () => {
        if (!cart?.id) {
            return;
        }

        setIsLoading(true);

        try {
            const result = await getEstimatedTreatsPointsForCart({
                cartId: cart?.id,
                loyaltyId: user?.loyaltyid,
            });

            setEstimatedTreatsPoints(result || null);
        } catch (error) {
            logConsoleError(JSON.stringify(error), {
                component: COMPONENTS.CART_PROVIDER,
                severity: 'MEDIUM',
                additionalInfo: 'Failed to get estimated treats points',
            });
        } finally {
            setIsLoading(false);
        }
    }, [cart, user]);

    useEffect(() => {
        const treatsPointsResponse = async () =>
            await getEstimatedTreatsPoints();

        treatsPointsResponse();
    }, [cart, getEstimatedTreatsPoints]);

    return {
        estimatedTreatsPoints,
        getEstimatedTreatsPoints,
        isGetEstimatedTreatsPointsLoading: isLoading,
    };
};

export default useTreatsInCart;
